/**
 * Guest Wi-Fi API client for Team Member approval flow
 * Enforces security requirements:
 * - Approved requests must include Authorization bearer token
 * - Non-approved requests work without token
 * - Explicit error handling for 401 auth failures
 */

import { logEvent, LogLevel } from '@praxis/component-logging'

/**
 * Custom error class for authentication failures
 */
export class AuthenticationError extends Error {
  constructor(message, statusCode = 401) {
    super(message)
    this.name = 'AuthenticationError'
    this.statusCode = statusCode
  }
}

/**
 * Submit Team Member data to the backend
 *
 * @param {Object} params
 * @param {string} params.apiUrl - API path or URL for the backend request
 * @param {string} params.status - Status to submit (Approved, Rejected, Time-Out, In Progress)
 * @param {string} params.flowId - Unique flow identifier
 * @param {string} params.macAddress - Device MAC address
 * @param {string} params.location - Device location/venue
 * @param {string} params.deviceType - Device type/user agent
 * @param {string} [params.accessToken] - IAM access token (required for Approved status)
 *
 * @returns {Promise<Object>} Backend response
 * @throws {AuthenticationError} When Approved request lacks valid bearer token (401)
 * @throws {Error} On other API failures
 */
export async function submitTeamMemberStatus({
  apiUrl,
  status,
  flowId,
  macAddress,
  location,
  deviceType,
  accessToken,
}) {
  if (!apiUrl) {
    throw new Error('API configuration missing (apiUrl)')
  }

  if (!flowId) {
    throw new Error('Missing flow_id')
  }

  if (!macAddress) {
    throw new Error('Missing MAC address')
  }

  // Approved requests MUST have a bearer token
  if (status === 'Approved' && !accessToken) {
    throw new AuthenticationError(
      'Authorization bearer token is required for Approved status',
      401,
    )
  }

  const payload = {
    flow_id: flowId,
    mac_address: macAddress,
    status,
    location,
    device_type: deviceType,
    // IMPORTANT: Do NOT include zid or email in the payload.
    // For Approved requests, the backend derives identity from the bearer token.
    // For other statuses, these fields are not needed.
  }

  const headers = {
    'Content-Type': 'application/json',
  }

  // Attach Authorization header only for Approved requests with valid token
  if (status === 'Approved' && accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  logEvent(
    {
      message: `tm_status flowId=${flowId} mac=${macAddress} status=${status}`,
    },
    { level: LogLevel.Info },
  )

  const response = await fetch(apiUrl, {
    method: 'POST',
    // Don't keep alive for Approved; connection may need to close before redirect
    keepalive: status !== 'Approved',
    headers,
    body: JSON.stringify(payload),
  })

  // Handle 401 as explicit authentication failure
  if (response.status === 401) {
    logEvent(
      {
        message: `[TM] 401 Unauthorized for status=${status}`,
      },
      { level: LogLevel.Error },
    )
    throw new AuthenticationError(
      'Your sign-in session expired or is invalid. Please sign in again.',
      401,
    )
  }

  // Handle other error statuses
  if (!response.ok) {
    const errorText = await response.text()
    logEvent(
      {
        message: `[TM] API error: status=${response.status} body=${errorText}`,
      },
      { level: LogLevel.Error },
    )
    throw new Error(
      `Team Member service error (${response.status}): ${errorText}`,
    )
  }

  const data = await response.json()
  if (data?.status !== 'success') {
    throw new Error(data?.message || 'Backend update failed')
  }

  return data
}

/**
 * Extract IAM access token from @praxis/component-auth session
 *
 * @param {Object} session - Session object from useAuth()
 * @returns {string|null} Bearer token value or null if not available
 */
export function extractAccessToken(session) {
  // @praxis/component-auth typically stores tokens in session.tokens or session.accessToken
  // Adjust based on your actual Praxis auth component structure
  if (!session) {
    logEvent(
      { message: '[TM] extractAccessToken: session is null' },
      { level: LogLevel.Warning },
    )
    return null
  }

  // Try multiple common paths for Praxis auth tokens
  // Most common locations where Praxis stores the access token
  const token =
    session?.tokens?.accessToken ||
    session?.tokens?.access ||
    session?.accessToken ||
    session?.access_token ||
    session?.token ||
    session?.id_token ||
    session?.tokens?.[0]?.accessToken ||
    session?.tokens?.[0]

  if (!token) {
    logEvent(
      {
        message: '[TM] extractAccessToken: No token found in session',
        sessionKeys: Object.keys(session || {}),
      },
      { level: LogLevel.Warning },
    )
  }

  return token || null
}
